import React from 'react'
import { Drawer, Descriptions, Divider, Space, Button, Tag, Badge } from 'antd'
import { SendOutlined } from '@ant-design/icons'
import type { Notification } from '../types/notification.types'

interface NotificationDetailsDrawerProps {
    open: boolean
    onClose: () => void
    notification: Notification | null
    onRetry: (notification: Notification) => void
}

export const NotificationDetailsDrawer: React.FC<NotificationDetailsDrawerProps> = ({
    open,
    onClose,
    notification,
    onRetry
}) => {
    if (!notification) return null

    return (
        <Drawer
            title="Notification Details"
            placement="right"
            width={600}
            onClose={onClose}
            open={open}
        >
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <Descriptions title="Notification Information" column={1} bordered>
                    <Descriptions.Item label="Notification ID">
                        <strong>{notification.id}</strong>
                    </Descriptions.Item>
                    <Descriptions.Item label="Title">{notification.title}</Descriptions.Item>
                    <Descriptions.Item label="Message">
                        {notification.message}
                    </Descriptions.Item>
                    <Descriptions.Item label="Type">
                        <Tag
                            color={
                                notification.type === 'success'
                                    ? 'green'
                                    : notification.type === 'warning'
                                        ? 'orange'
                                        : notification.type === 'error'
                                            ? 'red'
                                            : 'blue'
                            }
                        >
                            {notification.type.toUpperCase()}
                        </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Recipient">
                        {notification.recipient}
                    </Descriptions.Item>
                    <Descriptions.Item label="Channel">
                        {notification.channel === 'email' && '📧 Email'}
                        {notification.channel === 'sms' && '💬 SMS'}
                        {notification.channel === 'push' && '🔔 Push'}
                        {notification.channel === 'in-app' && '📱 In-App'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Status">
                        <Badge
                            status={
                                notification.status === 'sent' || notification.status === 'read'
                                    ? 'success'
                                    : notification.status === 'failed'
                                        ? 'error'
                                        : 'processing'
                            }
                            text={notification.status.toUpperCase()}
                        />
                    </Descriptions.Item>
                    <Descriptions.Item label="Created At">
                        {notification.createdAt}
                    </Descriptions.Item>
                    {notification.sentAt && (
                        <Descriptions.Item label="Sent At">
                            {notification.sentAt}
                        </Descriptions.Item>
                    )}
                    {notification.failureReason && (
                        <Descriptions.Item label="Failure Reason">
                            <Tag color="red">{notification.failureReason}</Tag>
                        </Descriptions.Item>
                    )}
                </Descriptions>

                <Divider />

                <Space>
                    {(notification.status === 'pending' || notification.status === 'failed') && (
                        <Button
                            type="primary"
                            icon={<SendOutlined />}
                            onClick={() => onRetry(notification)}
                        >
                            Retry Notification
                        </Button>
                    )}
                </Space>
            </Space>
        </Drawer>
    )
}
